import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../../../../context/ThemeContext";
import { Mail, Phone, MapPin, Send, MessageSquare, Clock, CheckCircle } from "lucide-react";

const ContactPage = () => {
  const { isDarkTheme } = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
      
      // Reset success message after 5 seconds
      setTimeout(() => setIsSubmitted(false), 5000);
    }, 2000);
  };

  const contactInfo = [
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email Us",
      details: "admin@og-gpt.com",
      description: "Get in touch with our support team"
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Call Us",
      details: "+92-334-204-6861",
      description: "Speak with our experts directly"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Visit Us",
      details: "A-30 Memon Nagar, Qasimabad Hyderabad, Sindh, Pakistan",
      description: "Our office location for in-person visits"
    }
  ];

  const features = [
    {
      icon: <MessageSquare className="w-5 h-5" />,
      title: "24/7 Support",
      description: "Round-the-clock assistance for your needs"
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "Quick Response",
      description: "Get replies within 2-4 hours"
    },
    {
      icon: <CheckCircle className="w-5 h-5" />,
      title: "Expert Team",
      description: "Petroleum engineering specialists"
    }
  ];

  return (
    <div className={`min-h-screen ${isDarkTheme ? 'bg-black' : 'bg-gray-50'}`}>
      {/* Header Section */}
      <motion.section
        className={`py-20 px-4 ${
          isDarkTheme 
            ? 'bg-gradient-to-r from-black/90 to-black/80' 
            : 'bg-gradient-to-l from-amber-300/60 to-amber-500'
        }`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Floating Elements */}
        <div className={`absolute right-0 top-0 w-32 h-32 ${
          isDarkTheme 
            ? 'bg-amber-900/10' 
            : 'bg-amber-700/10'
        } blur-3xl animate-float`} />
        <div className={`absolute left-0 bottom-0 w-40 h-40 ${
          isDarkTheme 
            ? 'bg-amber-900/10' 
            : 'bg-amber-700/10'
        } blur-3xl animate-float-delayed`} />

        <div className="max-w-7xl mx-auto relative">
          <motion.div
            className="text-center"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className={`text-4xl lg:text-5xl font-bold mb-6 ${
              isDarkTheme 
                ? 'text-amber-400' 
                : 'text-white'
            }`}>
              Get in Touch
            </h1>
            <p className={`text-xl max-w-2xl mx-auto ${
              isDarkTheme 
                ? 'text-gray-300' 
                : 'text-white/90'
            }`}>
              Have questions about OGFLEET? We're here to help you transform your 
              petroleum engineering operations with AI-powered solutions.
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/* Main Content */}
      <section className={`py-20 px-4 ${isDarkTheme ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <motion.div
              className="lg:col-span-1"
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className={`text-3xl font-bold mb-8 ${
                isDarkTheme 
                  ? 'text-amber-400' 
                  : 'text-amber-600'
              }`}>
                Contact Information
              </h2>
              
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    className={`p-6 rounded-xl border ${
                      isDarkTheme
                        ? 'bg-gray-900/50 border-gray-800 text-white hover:bg-gray-800/50'
                        : 'bg-white border-gray-200 text-black hover:bg-amber-50'
                    } transition-all duration-300`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className={`p-3 rounded-lg mb-4 inline-block ${
                      isDarkTheme 
                        ? 'bg-amber-400/20 text-amber-400' 
                        : 'bg-amber-100 text-amber-600'
                    }`}>
                      {info.icon}
                    </div>
                    <h3 className={`text-lg font-bold mb-2 ${
                      isDarkTheme 
                        ? 'text-amber-400' 
                        : 'text-amber-600'
                    }`}>
                      {info.title}
                    </h3>
                    <p className={`font-semibold mb-1 ${
                      isDarkTheme 
                        ? 'text-white' 
                        : 'text-gray-800'
                    }`}>
                      {info.details}
                    </p>
                    <p className={`text-sm ${
                      isDarkTheme 
                        ? 'text-gray-300' 
                        : 'text-gray-600'
                    }`}>
                      {info.description}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Features */}
              <div className="mt-12">
                <h3 className={`text-xl font-bold mb-6 ${
                  isDarkTheme 
                    ? 'text-amber-400' 
                    : 'text-amber-600'
                }`}>
                  Why Choose Us
                </h3>
                <div className="space-y-4">
                  {features.map((feature, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                    >
                      <div className={`p-2 rounded-lg ${
                        isDarkTheme 
                          ? 'bg-amber-400/20 text-amber-400' 
                          : 'bg-amber-100 text-amber-600'
                      }`}>
                        {feature.icon}
                      </div>
                      <div>
                        <h4 className={`font-semibold ${
                          isDarkTheme 
                            ? 'text-white' 
                            : 'text-gray-800'
                        }`}>
                          {feature.title}
                        </h4>
                        <p className={`text-sm ${
                          isDarkTheme 
                            ? 'text-gray-300' 
                            : 'text-gray-600'
                        }`}>
                          {feature.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              className="lg:col-span-2"
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className={`p-8 rounded-2xl shadow-xl border ${
                isDarkTheme
                  ? 'bg-gray-900/50 border-gray-800 text-white'
                  : 'bg-white border-gray-200 text-black'
              }`}>
                <h2 className={`text-3xl font-bold mb-8 ${
                  isDarkTheme 
                    ? 'text-amber-400' 
                    : 'text-amber-600'
                }`}>
                  Send us a Message
                </h2>

                {isSubmitted ? (
                  <motion.div
                    className="text-center py-12"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className={`p-4 rounded-full mb-4 inline-block ${
                      isDarkTheme 
                        ? 'bg-green-400/20 text-green-400' 
                        : 'bg-green-100 text-green-600'
                    }`}>
                      <CheckCircle className="w-12 h-12" />
                    </div>
                    <h3 className={`text-2xl font-bold mb-2 ${
                      isDarkTheme 
                        ? 'text-green-400' 
                        : 'text-green-600'
                    }`}>
                      Message Sent Successfully!
                    </h3>
                    <p className={`${
                      isDarkTheme 
                        ? 'text-gray-300' 
                        : 'text-gray-600'
                    }`}>
                      Thank you for contacting us. We'll get back to you within 2-4 hours.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                          isDarkTheme 
                            ? 'text-gray-300' 
                            : 'text-gray-700'
                        }`}>
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className={`w-full px-4 py-3 rounded-lg border transition-colors ${
                            isDarkTheme
                              ? 'bg-gray-800 border-gray-700 text-white focus:border-amber-400 focus:ring-amber-400/20'
                              : 'bg-gray-50 border-gray-300 text-black focus:border-amber-500 focus:ring-amber-500/20'
                          } focus:outline-none focus:ring-2`}
                          placeholder="Enter your full name"
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                          isDarkTheme 
                            ? 'text-gray-300' 
                            : 'text-gray-700'
                        }`}>
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className={`w-full px-4 py-3 rounded-lg border transition-colors ${
                            isDarkTheme
                              ? 'bg-gray-800 border-gray-700 text-white focus:border-amber-400 focus:ring-amber-400/20'
                              : 'bg-gray-50 border-gray-300 text-black focus:border-amber-500 focus:ring-amber-500/20'
                          } focus:outline-none focus:ring-2`}
                          placeholder="Enter your email address"
                        />
                      </div>
                    </div>

                    <div>
                      <label className={`block text-sm font-semibold mb-2 ${
                        isDarkTheme 
                          ? 'text-gray-300' 
                          : 'text-gray-700'
                      }`}>
                        Subject *
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 rounded-lg border transition-colors ${
                          isDarkTheme
                            ? 'bg-gray-800 border-gray-700 text-white focus:border-amber-400 focus:ring-amber-400/20'
                            : 'bg-gray-50 border-gray-300 text-black focus:border-amber-500 focus:ring-amber-500/20'
                        } focus:outline-none focus:ring-2`}
                        placeholder="What's this about?"
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-semibold mb-2 ${
                        isDarkTheme 
                          ? 'text-gray-300' 
                          : 'text-gray-700'
                      }`}>
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        className={`w-full px-4 py-3 rounded-lg border transition-colors resize-none ${
                          isDarkTheme
                            ? 'bg-gray-800 border-gray-700 text-white focus:border-amber-400 focus:ring-amber-400/20'
                            : 'bg-gray-50 border-gray-300 text-black focus:border-amber-500 focus:ring-amber-500/20'
                        } focus:outline-none focus:ring-2`}
                        placeholder="Tell us more about your inquiry..."
                      />
                    </div>

                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className={`group relative overflow-hidden px-8 py-4 rounded-full font-bold text-lg shadow-lg transition-all duration-300 flex items-center gap-3 ${
                        isSubmitting
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      } ${
                        isDarkTheme
                          ? 'bg-gradient-to-r from-amber-400 to-amber-600 hover:from-amber-500 hover:to-amber-700 text-white'
                          : 'bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 text-white'
                      }`}
                      whileHover={!isSubmitting ? { scale: 1.05 } : {}}
                      whileTap={!isSubmitting ? { scale: 0.95 } : {}}
                    >
                      <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                      {!isSubmitting && (
                        <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      )}
                      
                      {/* Button shine effect */}
                      {!isSubmitting && (
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                          initial={{ x: '-100%' }}
                          whileHover={{ x: '100%' }}
                          transition={{ duration: 0.6 }}
                        />
                      )}
                    </motion.button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
